import * as K from 'common/constants';

export enum AUTH_PAGE_LOGIN_TYPE {
  SSO = 'sso',
  EMAIL_PASSWORD = 'email-password',
  SSO_LOGIN_AS = 'ssoLoginAs',
}

export const XSRF_NOT_LOGGED_IN = 'xsrf_not_logged_in';

export const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  // General error messages for both SSO log in and standard log in

  // User denied because they have no Access
  noAccess: 'Your user account does not have access. Talk to your ' + K.PRODUCT_NAME + ' administrator.',
  // SSOLogin - This user MUST use SSO to access Scalyr
  ssoRequired:
    "The email address entered is associated with an SSO domain. Please click on 'Log in using SSO' to access " +
    K.PRODUCT_NAME,
  // SSOLogin - User needs an SSO invitation to access Scalyr via SSO
  ssoInvitationRequired:
    'This email address has not been registered with ' +
    K.PRODUCT_NAME +
    '. Please ask an administrator to invite you to a ' +
    K.PRODUCT_NAME +
    ' team before you access ' +
    K.PRODUCT_NAME +
    '.',
  // SSOLogin - User must log in using the normal Scalyr log in
  standardLoginRequired:
    'This email address is not associated with an SSO domain. Please double-check the address, or click below to log in using your ' +
    K.PRODUCT_NAME +
    ' password. If you are interested in setting up SSO support for ' +
    K.PRODUCT_NAME +
    ' at your organization, contact Support.',
  // LoginForm - The email address and/or password provided is either empty or not valid
  invalidLoginCredentials: 'Your login attempt has failed. Please check your username and password and try again.',
  // Server restarted recently, and is not yet ready for requests
  databaseNotReady:
    'The server was recently restarted and is not yet ready for requests. Please try again in about a minute.',
  // User is in the forbidSiteUsers list and is not allowed to use the app.
  accountLockedOut: 'This account is locked out. Please contact an adminsitrator',
  unknownError: 'Unknown error occurred. Please contact Support.',
  // SSO Specific error messages
  // Something went wrong in processing the SAMLResponse. Likely due to ill-formatted response or a response that is
  // inconsistent with the authentication request sent by Scalyr.
  invalidResponse:
    'There was an issue processing the SAML response. Please try again. If the problem persists, please contact Support.',
  authenticationFailure:
    'There was a problem authenticating the email address through Single Sign On. Please try again.',
  errorSendingRequest:
    'There was a problem sending an authentication request please try again. If the problem persists, please contact Support.',
  // Thrown when an SSO user attempts to accept a regular Scalyr invitation and is not on a white-list.
  invalidInvitation:
    'The invitation is no longer valid. Please contact an administrator for an invitation to ' + K.PRODUCT_NAME + '.',
  // There was a problem reconfirming the user.
  errorReAuthenticatingUser:
    'There was a problem re-authenticating this email address through SSO please try again. If the problem persists, please contact an administrator to invite you to a ' +
    K.PRODUCT_NAME +
    ' team before you access ' +
    K.PRODUCT_NAME +
    '.',
  // There was a problem with the metadata
  errorParsingMetadata:
    'There was a problem parsing the metadata associated with this email address. Please contact Support',
  // Thrown when the token cannot be verified for solicited SSO
  invalidToken: 'An unexpected token error occurred. Please contact Support',
  // The IdP was not configured correctly on Scalyr's end
  invalidIdPConfiguration:
    'There was a problem parsing the identity provider details for your organization. Please contact Support.',
  // SSO - SAML 2.0 Specific error messages
  // Method of the subject confirmation data must equal urn:oasis:names:tc:SAML:2.0:cm:bearer in the assertion.
  SAMLInvalidAssertionBearer:
    "The subject confirmation data 'method' must be a SAML 2.0 bearer. Please talk to your SSO administrator or contact Support for assistance.",
  // SAML Response received outside of a valid time interval as specified by conditions OR as determined by authentication request send and response received time
  SAMLExpiredAssertion:
    'The SAML response has expired. Please try logging in again. If the problem continues, contact Support for assistance.',
  // IdP intended to send the SAML Assertion to a different url than Scalyr's. If a user receives this error, they should
  // verify that they have recorded the ACS Url correctly in the IdP configuration of Scalyr as an SP.
  SAMLInvalidACSUrl:
    'Consumer service URL does not belong to ' +
    K.PRODUCT_NAME +
    '. Please talk to your SSO administrator or contact Support for assistance.',
  // The user agent is specified in the SAML assertion but it does not match the user agent used to send the authnRequest.
  SAMLInvalidUserAgent:
    'User agent in the SAML response does not match the sender of the authentication request. Please talk to your SSO administrator or contact Support for assistance.',
  // Audience URI does not match Scalyr's SP Entity Id. Audience URI is typically a property the IdP must set when configuring a service party.
  SAMLInvalidAudienceURI:
    'Audience URI in the SAML assertion must match ' +
    K.PRODUCT_NAME +
    "'s SP Entity Id. Please talk to your SSO administrator or contact Support for assistance.",
  // Thrown when the SAML Response contains an assertion that is not signed
  SAMLAssertionMustBeSigned:
    'SAML assertion must be signed; please talk to your SSO administrator or contact Support for assistance',
  // User has been rate limited for making too many requests
  ratelimitError: 'Too many requests for [login] from this IP address. Please try again after sometime',
};

export enum SSO_INVITE_TOKEN_ERRORS {
  // bad invitation url
  badInvitationUrl = 'badInvitationUrl',
  // currently logged user opened invite for another account - should logout
  incorrectInvitee = 'incorrectInvitee',
  // invite for such account has never been sent
  invalidInvitee = 'invalidInvitee',
  // invalid sso invitee
  invalidSsoInvitee = 'invalidSsoInvitee',
  // bad idP configuration
  badIdPConfiguration = 'badIdPConfiguration',
  // could not link account
  couldNotLink = 'couldNotLink',
  // unknown error
  unknown = 'unknown',
}
